import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { panelThemeClasses } from "../../theme/themeClasses";
import { Badge, type Tone } from "../ui";

export interface DashboardStat {
  label: string;
  value: string;
  icon: string;
  change?: string;
  positive?: boolean;
}

export interface DashboardChartPoint {
  name: string;
  value: number;
}

export const RECENT_ITEM_TONES = {
  ok: "primary",
  warn: "warning",
  info: "info",
  danger: "error",
  muted: "neutral",
} as const satisfies Record<string, Tone>;

export type RecentItemTone = keyof typeof RECENT_ITEM_TONES;

export interface RecentItem {
  primary: string;
  secondary: string;
  badge?: string;
  tone?: RecentItemTone;
}

interface FeatureDashboardProps {
  title: string;
  subtitle?: string;
  icon?: string;
  stats: DashboardStat[];
  chart?: { title: string; data: DashboardChartPoint[] };
  recent?: { title: string; items: RecentItem[] };
  noticeKey: string;
}

export default function FeatureDashboard({
  title,
  subtitle,
  icon,
  stats,
  chart,
  recent,
  noticeKey,
}: FeatureDashboardProps) {
  const { t } = useTranslation("common");
  const tc = panelThemeClasses();

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center gap-3">
          {icon && (
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className={`${icon} text-xl`} />
            </span>
          )}
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>{title}</h1>
            {subtitle && <p className={`text-sm ${tc.textSecondary}`}>{subtitle}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}
            >
              <div className="mb-2 flex items-center justify-between">
                <span className={`text-sm ${tc.textSecondary}`}>{s.label}</span>
                <i className={`${s.icon} text-xl text-primary-500`} />
              </div>
              <div className={`text-2xl font-bold ${tc.text}`}>{s.value}</div>
              {s.change && (
                <div
                  className={`mt-1 text-xs ${s.positive ? "text-primary-500" : "text-error"}`}
                >
                  <i className={s.positive ? "ri-arrow-up-line" : "ri-arrow-down-line"} />{" "}
                  {s.change}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {chart && (
            <div
              className={`rounded-xl border p-5 lg:col-span-2 ${tc.cardBorder} ${tc.cardBg}`}
            >
              <h3 className={`mb-4 text-sm font-bold ${tc.text}`}>{chart.title}</h3>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={chart.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--c-border))" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12, fill: "rgb(var(--c-content-muted))" }}
                    />
                    <YAxis tick={{ fontSize: 12, fill: "rgb(var(--c-content-muted))" }} />
                    <Tooltip
                      contentStyle={{
                        background: "rgb(var(--c-card))",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="value" fill="rgb(var(--c-primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {recent && (
            <div className={`rounded-xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
              <h3 className={`mb-4 text-sm font-bold ${tc.text}`}>{recent.title}</h3>
              <div className="space-y-3">
                {recent.items.map((it, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg p-3 bg-surface"
                  >
                    <div className="min-w-0">
                      <p className={`truncate text-sm font-medium ${tc.text}`}>{it.primary}</p>
                      <p className={`truncate text-xs ${tc.textSecondary}`}>{it.secondary}</p>
                    </div>
                    {it.badge && (
                      <Badge
                        tone={RECENT_ITEM_TONES[it.tone ?? "info"]}
                        className="ml-2 whitespace-nowrap"
                      >
                        {it.badge}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <p className={`mt-4 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          {t(noticeKey)}
        </p>
      </div>
    </div>
  );
}
